'use client';

import { cloneElement, forwardRef, isValidElement, useEffect } from 'react';
import { type IntersectionOptions, useInView } from 'react-intersection-observer';
import { useAnalytics } from '../hooks/useAnalytics';
import { getCleanLocationHref } from '../lib/urlUtils';
import { isForwardRefComponent } from '../lib/utils';
import { EventName, type FeatureViewedPayload } from '../Shibuya';
import { useAnalyticsPageView } from './PageView/Context';

export interface AnalyticsFeatureViewedProps {
  entityName: FeatureViewedPayload['entity_name'];
  entitySection?: FeatureViewedPayload['entity_section'];
  impressionPosition?: FeatureViewedPayload['impression_position'];
  additionalAttributes?: FeatureViewedPayload['additional_attributes'];
  scrollableAncestor?: HTMLElement;
  activityId?: FeatureViewedPayload['activity_id'];
  parentActivityId?: FeatureViewedPayload['parent_activity_id'];
  inViewOptions?: IntersectionOptions;
  children?: React.ReactNode;
}

function hasParentActivityPayload(props: AnalyticsFeatureViewedProps) {
  return props.parentActivityId != null || props.activityId != null;
}

export const AnalyticsFeatureViewed = forwardRef<HTMLElement, AnalyticsFeatureViewedProps>(
  (props, ref) => {
    const {
      children,
      scrollableAncestor,
      inViewOptions,
      entityName,
      entitySection,
      impressionPosition,
      additionalAttributes,
      activityId,
      parentActivityId,
    } = props;

    const hasParentActivity = hasParentActivityPayload(props);

    const { analytics, deployableName, deployableVersion, deviceId, gitSha } = useAnalytics();
    const { pageViewId, pageViewReady } = useAnalyticsPageView();

    const inViewConfig: IntersectionOptions = hasParentActivity
      ? { rootMargin: '1px', threshold: 0, triggerOnce: true, ...inViewOptions }
      : {
          root: scrollableAncestor ? scrollableAncestor || 'window' : null,
          threshold: 0.01,
          triggerOnce: true,
          ...inViewOptions,
        };

    const { ref: inViewRef, inView } = useInView(inViewConfig);

    const setRefs = (node: HTMLElement | null) => {
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref && 'current' in ref) {
        ref.current = node;
      }
      inViewRef(node);
    };

    useEffect(() => {
      if (!pageViewReady || !deviceId || !inView) {
        return;
      }

      const context = {
        app: {
          name: deployableName,
          version: gitSha,
        },
      };

      const eventPayload: FeatureViewedPayload = {
        deployable_name: deployableName,
        deployable_version: deployableVersion,
        entity_name: entityName,
        entity_section: entitySection,
        impression_page_url: getCleanLocationHref(),
        impression_pageview_id: pageViewId,
        impression_position: impressionPosition,
        traditional_device_id: deviceId,
      };

      if (additionalAttributes) {
        eventPayload.additional_attributes = additionalAttributes;
      }

      if (hasParentActivity) {
        analytics?.track(
          EventName.FEATURE_VIEWED,
          {
            ...eventPayload,
            activity_id: activityId,
            parent_activity_id: parentActivityId,
          },
          { context },
        );
        return;
      }

      analytics?.track(EventName.FEATURE_VIEWED, eventPayload, { context });
    }, [
      hasParentActivity,
      pageViewReady,
      deviceId,
      inView,
      analytics,
      pageViewId,
      deployableName,
      deployableVersion,
      gitSha,
      entityName,
      entitySection,
      impressionPosition,
      additionalAttributes,
      activityId,
      parentActivityId,
    ]);

    if (hasParentActivity) {
      if (!isValidElement(children)) {
        throw new Error(
          'AnalyticsFeatureViewed with activityId/parentActivityId requires a single React element child',
        );
      }
      return cloneElement(children as React.ReactElement<{ ref: unknown }>, { ref: setRefs });
    }

    if (!children) {
      throw new Error('Missing Child');
    }

    return isForwardRefComponent(children) ? (
      cloneElement(children as React.ReactElement<{ ref: unknown }>, {
        ref: setRefs,
      })
    ) : (
      <div ref={setRefs}>{children}</div>
    );
  },
);
